import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router'
import { fetchAislePrimaryProducts, type AisleParams } from 'legacy/common/api'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { errors } from 'utils/error-handling/errors'
import { useFetchPrimaryAisleProductCountLazyQuery } from '__codegen__/api'

interface State {
  loading: boolean
  error: boolean
  hasPrimaryProducts: boolean
}

export const useGetPrimaryProductsForAisle = (useMesh?: boolean): State => {
  const params = useParams<AisleParams>()
  const { notifyError } = useNotifications()

  const [state, setState] = useState<State>({
    loading: false,
    error: false,
    hasPrimaryProducts: false,
  })

  const [executeQuery, { loading, error, data }] = useFetchPrimaryAisleProductCountLazyQuery()

  // Handles lazy query execution when params change, only when using mesh
  useEffect(() => {
    if (!useMesh) return

    executeQuery({
      variables: {
        primaryAisleId: params.aisle_id,
        retailerId: params.warehouse_id,
      },
    })
  }, [params, executeQuery, useMesh])

  // Handles inner state when using mesh
  useEffect(() => {
    if (!useMesh) return

    setState({
      loading,
      error: Boolean(error),
      hasPrimaryProducts:
        (parseInt(data?.retailerToolsServiceGetRetailerProducts?.totalCount) ?? 0) > 0,
    })
  }, [loading, error, data, useMesh])

  // Handles inner state when using legacy endpoints. TODO: remove me eventually!
  useEffect(() => {
    if (useMesh) return

    const fetchRes = async () => {
      try {
        setState({
          loading: true,
          error: false,
          hasPrimaryProducts: false,
        })

        const res = await fetchAislePrimaryProducts(params)

        setState({
          loading: false,
          error: false,
          hasPrimaryProducts: res.primary_product_ids.length > 0,
        })
      } catch (e) {
        setState({
          loading: false,
          error: true,
          hasPrimaryProducts: false,
        })
      }
    }

    fetchRes()
  }, [params, useMesh])

  // Handle error states
  useEffect(() => {
    if (!state.error) return

    notifyError(
      <FormattedMessage
        id="aisles.onlyPrimaryProducts.error"
        data-testid="get-primary-products-for-aisle-error"
      />
    )

    errors.captureMessage(
      `Unable to load primary products for aisle: ${params.aisle_id}, using mesh?: ${useMesh}`
    )
  }, [state.error, notifyError, params.aisle_id, useMesh])

  return state
}
